/**
 * Damage marked on the floor plan (PRP-SKETCH-FIELD-5 L1, L2, L4).
 *
 * One record of damage: a room's `damage` is the damage half of its checklist record
 * (`RoomChecklistCapture`: `damagedAreas`, `wallSpans`, `waterLineIn`, `waterLineScope`,
 * `markedOn`). planStore reads and writes it on `OfflineRoom.checklist`; nothing here is
 * exported to the ESX (L3) or touches geometry.
 *
 * - Spans are stored by POSITION, not by segment id: segments are derived (FIELD-4 K6). A
 *   span is `{a, b, fromFt, toFt}`: the corner ids of the wall and the damaged stretch along
 *   it from `a`, interior face. Tapping a segment marks that segment's full stretch.
 * - `walls` in `damagedAreas` with no span means every wall (a checklist tick). The first
 *   segment marked after that narrows it to that segment. Marking a span always puts `walls`
 *   in `damagedAreas`; clearing the last span takes it out again unless the walls mark was
 *   made on the checklist (`markedOn: 'checklist'`), in which case it means every wall.
 * - When geometry changes, `reconcileDamage` clips spans to the wall's new length, lets a
 *   span that covered the whole wall follow the wall, and drops (and names) a span whose
 *   wall is gone.
 * - Every action mutates the plan it is given and returns `{ ok: true }` only when it changed
 *   it (core convention).
 */
import { POLICY } from './policy';
import { EPS, area, dist, findRoom, outN, unit } from './geom';
import { edgeSegments, segmentAt } from './segments';
import type { DamageMarks, Fail, Plan, Pt, Room } from './types';
import type { WallSpan } from '../../../storage/schemas';

/** The checklist area keys damage marks write (app/services/scope_engine/checklist/areas.py): a data contract, not a tunable. */
export const DAMAGE_AREAS = { walls: 'walls', ceiling: 'ceiling', floor: 'floor' } as const; // policy-literal-ok: vocabulary
const AREAS = () => DAMAGE_AREAS;
const TOL = () => POLICY.damage.whole_wall_tol_ft;
const MIN = () => POLICY.damage.span_min_ft;
const idKey = (id: number): string => String(id);

// ---------------------------------------------------------------- reading a room's marks

/** The room's marks, never undefined (a room with no marks reads as empty). */
export function marksOf(r: Room): DamageMarks {
  const d = r.damage;
  return {
    damagedAreas: [...(d?.damagedAreas ?? [])],
    ...(d?.wallSpans ? { wallSpans: d.wallSpans.map((s) => ({ ...s })) } : {}),
    ...(d?.waterLineIn != null ? { waterLineIn: d.waterLineIn } : {}),
    ...(d?.waterLineScope ? { waterLineScope: d.waterLineScope } : {}),
    ...(d?.markedOn ? { markedOn: d.markedOn } : {}),
  };
}

export const hasArea = (r: Room, area: string): boolean => !!r.damage?.damagedAreas?.includes(area);
export const hasAnyDamage = (r: Room): boolean => (r.damage?.damagedAreas?.length ?? 0) > 0 || (r.damage?.wallSpans?.length ?? 0) > 0;

/** A wall of a room: its start corner id and its drawn length. */
export interface WallExtent { vid: number; a: Pt; b: Pt; lengthFt: number }

/** Every real wall (not a zero-length edge) of the room, in corner order. */
export function roomWalls(r: Room): WallExtent[] {
  const n = r.corners.length;
  const out: WallExtent[] = [];
  for (let i = 0; i < n; i++) {
    const a = r.corners[i], b = r.corners[(i + 1) % n];
    const lengthFt = dist(a, b);
    if (lengthFt > EPS) out.push({ vid: a.id, a, b, lengthFt });
  }
  return out;
}

/** A span read against the room's wall, in the wall's own direction (start corner to next corner). */
export interface ResolvedSpan { span: WallSpan; vid: number; fromFt: number; toFt: number; lengthFt: number }

/** The wall a span names, whichever way round the corner pair is stored; null when the wall is gone. */
export function resolveSpan(r: Room, span: WallSpan): ResolvedSpan | null {
  const n = r.corners.length;
  for (let i = 0; i < n; i++) {
    const a = r.corners[i], b = r.corners[(i + 1) % n];
    const lengthFt = dist(a, b);
    if (lengthFt <= EPS) continue;
    if (idKey(a.id) === span.a && idKey(b.id) === span.b) return { span, vid: a.id, fromFt: span.fromFt, toFt: span.toFt, lengthFt };
    // The room was mirrored: the wall runs the other way now, so positions count from the other end.
    if (idKey(b.id) === span.a && idKey(a.id) === span.b) {
      return { span, vid: a.id, fromFt: lengthFt - span.toFt, toFt: lengthFt - span.fromFt, lengthFt };
    }
  }
  return null;
}

/** 'all': `walls` ticked with no span (every wall); 'spans': the stretches in `wallSpans`; 'none'. */
export type WallMode = 'none' | 'all' | 'spans';

export function wallMode(r: Room): WallMode {
  if ((r.damage?.wallSpans?.length ?? 0) > 0) return 'spans';
  return hasArea(r, AREAS().walls) ? 'all' : 'none';
}

/** The damaged stretches of the room's walls, in wall direction ('all' reads as every wall in full). */
export function damagedStretches(r: Room): ResolvedSpan[] {
  const mode = wallMode(r);
  if (mode === 'none') return [];
  if (mode === 'all') {
    return roomWalls(r).map((w) => ({
      span: { a: idKey(w.vid), b: idKey(nextCornerId(r, w.vid)), fromFt: 0, toFt: w.lengthFt },
      vid: w.vid, fromFt: 0, toFt: w.lengthFt, lengthFt: w.lengthFt,
    }));
  }
  return (r.damage?.wallSpans ?? []).map((s) => resolveSpan(r, s)).filter((x): x is ResolvedSpan => !!x);
}

function nextCornerId(r: Room, vid: number): number {
  const i = r.corners.findIndex((c) => c.id === vid);
  return r.corners[(i + 1) % r.corners.length].id;
}

/** Damaged wall length, feet. */
export function damagedWallFt(r: Room): number {
  return damagedStretches(r).reduce((s, x) => s + Math.max(0, x.toFt - x.fromFt), 0);
}

// ---------------------------------------------------------------- intervals

type Iv = [number, number];

/** Sorted, with overlapping or touching (within the minimum span) intervals joined. */
function union(ivs: Iv[]): Iv[] {
  const s = ivs.filter(([a, b]) => b > a).sort((x, y) => x[0] - y[0]);
  const out: Iv[] = [];
  for (const [a, b] of s) {
    const last = out[out.length - 1];
    if (last && a <= last[1] + EPS) last[1] = Math.max(last[1], b); else out.push([a, b]);
  }
  return out;
}

function subtract(ivs: Iv[], [c0, c1]: Iv): Iv[] {
  const out: Iv[] = [];
  for (const [a, b] of union(ivs)) {
    if (c1 <= a || c0 >= b) { out.push([a, b]); continue; }
    if (c0 > a) out.push([a, c0]);
    if (c1 < b) out.push([c1, b]);
  }
  return out.filter(([a, b]) => b - a >= MIN());
}

const covers = (ivs: Iv[], [c0, c1]: Iv): boolean => union(ivs).some(([a, b]) => a <= c0 + TOL() && b >= c1 - TOL());

// ---------------------------------------------------------------- writing a room's marks

function ensure(r: Room): NonNullable<Room['damage']> {
  if (!r.damage) r.damage = { damagedAreas: [] };
  if (!r.damage.damagedAreas) r.damage.damagedAreas = [];
  return r.damage;
}

const addArea = (d: NonNullable<Room['damage']>, area: string) => { if (!d.damagedAreas.includes(area)) d.damagedAreas.push(area); };
const dropArea = (d: NonNullable<Room['damage']>, area: string) => { d.damagedAreas = d.damagedAreas.filter((a) => a !== area); };

/** The first value set on a level (a room whose scope is 'level'), or null. */
export function levelWaterLine(plan: Plan, level: string): number | null {
  for (const q of plan.rooms) {
    if (q.level === level && q.damage?.waterLineScope === 'level' && q.damage.waterLineIn != null) return q.damage.waterLineIn;
  }
  return null;
}

/** A room that has no water line of its own takes its level's value when damage is first marked on it. */
function inheritLevelWaterLine(plan: Plan, r: Room): void {
  const d = ensure(r);
  if (d.waterLineIn != null || d.waterLineScope === 'room') return;
  const v = levelWaterLine(plan, r.level);
  if (v != null) { d.waterLineIn = v; d.waterLineScope = 'level'; }
}

/** After the spans change: `walls` follows them (see the module note). */
function syncWalls(d: NonNullable<Room['damage']>): void {
  const A = AREAS();
  if ((d.wallSpans?.length ?? 0) > 0) { addArea(d, A.walls); return; }
  if (d.markedOn !== 'checklist') dropArea(d, A.walls);
}

const missingRoom = (): Fail<'missing'> => ({ ok: false, err: 'missing', message: 'That room is gone' });

const spanOf = (r: Room, vid: number, fromFt: number, toFt: number): WallSpan => ({
  a: idKey(vid), b: idKey(nextCornerId(r, vid)), fromFt, toFt,
});

/** The wall's spans in wall direction, as intervals. */
function wallIntervals(r: Room, vid: number): Iv[] {
  return (r.damage?.wallSpans ?? [])
    .map((s) => resolveSpan(r, s))
    .filter((x): x is ResolvedSpan => !!x && x.vid === vid)
    .map((x): Iv => [x.fromFt, x.toFt]);
}

/** Replace one wall's spans with `ivs`, keeping every other wall's. */
function setWallIntervals(r: Room, vid: number, ivs: Iv[]): void {
  const d = ensure(r);
  const others = (d.wallSpans ?? []).filter((s) => resolveSpan(r, s)?.vid !== vid);
  const mine = union(ivs).filter(([a, b]) => b - a >= MIN()).map(([a, b]) => spanOf(r, vid, a, b));
  d.wallSpans = [...others, ...mine];
}

export type MarkResult =
  | { ok: true; marked: boolean }
  | Fail<'missing' | 'no-wall' | 'no-segment'>;

/**
 * Damage mode, tap on a wall at `off` feet from its start corner: that segment's full stretch is
 * marked, or unmarked when it is already marked. With `walls` ticked and no span (every wall), the
 * tap narrows the mark to that segment.
 */
export function toggleSegment(plan: Plan, rid: string, vid: number, off: number): MarkResult {
  const r = findRoom(plan, rid);
  if (!r) return missingRoom();
  const wall = roomWalls(r).find((w) => w.vid === vid);
  if (!wall) return { ok: false, err: 'no-wall', message: 'That wall has no length' };
  const segs = edgeSegments(plan, rid, vid);
  const g = segs.length ? segmentAt(segs, off) : null;
  const seg: Iv = g ? [g.s0, g.s1] : [0, wall.lengthFt];
  if (seg[1] - seg[0] < MIN()) return { ok: false, err: 'no-segment', message: 'That stretch is too short to mark' };
  const d = ensure(r);
  // `walls` ticked on the checklist with no span means every wall (nothing in `cur` below); the first
  // segment marked then narrows it to that segment, since the spans are now the mark.
  const cur = wallIntervals(r, vid);
  const marked = !covers(cur, seg);
  setWallIntervals(r, vid, marked ? [...cur, seg] : subtract(cur, seg));
  d.markedOn = 'sketch';
  syncWalls(d);
  if (marked) inheritLevelWaterLine(plan, r);
  return { ok: true, marked };
}

/** True when every wall is damaged in full and the ceiling and floor are damaged. */
export function isWholeRoom(r: Room): boolean {
  const A = AREAS();
  if (!hasArea(r, A.ceiling) || !hasArea(r, A.floor) || !hasArea(r, A.walls)) return false;
  if (wallMode(r) === 'all') return true;
  const walls = roomWalls(r);
  return walls.length > 0 && walls.every((w) => covers(wallIntervals(r, w.vid), [0, w.lengthFt]));
}

/**
 * Whole room on or off. On: every wall in full length, the ceiling and the floor. Off: all three
 * cleared (other checklist areas are left as they are).
 */
export function toggleWholeRoom(plan: Plan, rid: string): { ok: true; on: boolean } | Fail<'missing'> {
  const r = findRoom(plan, rid);
  if (!r) return missingRoom();
  const A = AREAS();
  const d = ensure(r);
  const on = !isWholeRoom(r);
  if (on) {
    d.wallSpans = roomWalls(r).map((w) => spanOf(r, w.vid, 0, w.lengthFt));
    addArea(d, A.walls); addArea(d, A.ceiling); addArea(d, A.floor);
  } else {
    d.wallSpans = [];
    dropArea(d, A.walls); dropArea(d, A.ceiling); dropArea(d, A.floor);
  }
  d.markedOn = 'sketch';
  if (on) inheritLevelWaterLine(plan, r);
  return { ok: true, on };
}

/** Ceiling or floor damaged, on or off. */
export function toggleArea(plan: Plan, rid: string, area: 'ceiling' | 'floor'): { ok: true; on: boolean } | Fail<'missing'> {
  const r = findRoom(plan, rid);
  if (!r) return missingRoom();
  const key = AREAS()[area];
  const d = ensure(r);
  const on = !d.damagedAreas.includes(key);
  if (on) addArea(d, key); else dropArea(d, key);
  if (on) inheritLevelWaterLine(plan, r);
  return { ok: true, on };
}

// ---------------------------------------------------------------- water line (L2)

/** Set (or clear, with null) the water line of a whole level: every room on it whose scope is not 'room'. */
export function setLevelWaterLine(plan: Plan, level: string, inches: number | null): { ok: true; rooms: number } | Fail<'bad-value' | 'no-rooms'> {
  if (inches != null && !(inches >= POLICY.damage.water_line_min_in && inches <= POLICY.damage.water_line_max_in)) {
    return { ok: false, err: 'bad-value', message: 'That water line is out of range' };
  }
  const rooms = plan.rooms.filter((q) => q.level === level && q.damage?.waterLineScope !== 'room');
  if (!rooms.length) return { ok: false, err: 'no-rooms', message: 'No room on this level takes a level water line' };
  for (const q of rooms) {
    const d = ensure(q);
    if (inches == null) { delete d.waterLineIn; delete d.waterLineScope; } else { d.waterLineIn = inches; d.waterLineScope = 'level'; }
  }
  return { ok: true, rooms: rooms.length };
}

/** One room's own water line (scope 'room'); null drops the override and the room follows its level again. */
export function setRoomWaterLine(plan: Plan, rid: string, inches: number | null): { ok: true } | Fail<'missing' | 'bad-value'> {
  const r = findRoom(plan, rid);
  if (!r) return missingRoom();
  if (inches != null && !(inches >= POLICY.damage.water_line_min_in && inches <= POLICY.damage.water_line_max_in)) {
    return { ok: false, err: 'bad-value', message: 'That water line is out of range' };
  }
  const d = ensure(r);
  if (inches != null) { d.waterLineIn = inches; d.waterLineScope = 'room'; return { ok: true }; }
  delete d.waterLineIn;
  delete d.waterLineScope;
  const v = levelWaterLine(plan, r.level);
  if (v != null) { d.waterLineIn = v; d.waterLineScope = 'level'; }
  return { ok: true };
}

export const waterLineIn = (r: Room): number | null => r.damage?.waterLineIn ?? null;

/** "WL 24 in". */
export const waterLineLabel = (inches: number): string => POLICY.damage.water_line_label.replace('{in}', String(inches));

/** Does this project's loss type ask for a water line when Damage mode opens? */
export function isFloodLoss(lossType: string | null | undefined): boolean {
  const t = (lossType ?? '').toLowerCase();
  return !!t && POLICY.damage.flood_loss_types.some((x) => t.includes(x));
}

// ---------------------------------------------------------------- geometry changed

export interface DroppedSpan { rid: string; roomName: string; wall: string; fromFt: number; toFt: number }

/**
 * After a change, bring every room's spans in step with its walls (`before` is the plan as it was).
 * Mutates `after`. A span clips to its wall's new length; one that covered the whole wall follows the
 * wall; one whose wall is gone, or that clips away to nothing, is dropped and returned so the
 * caller can name it. `walls` leaves `damagedAreas` with the last span unless it was ticked on the
 * checklist.
 */
export function reconcileDamage(before: Plan, after: Plan): DroppedSpan[] {
  const dropped: DroppedSpan[] = [];
  for (const r of after.rooms) {
    const old = before.rooms.find((q) => q.id === r.id);
    // A room an action just made (a split's new half, a closet carved from a room) is cut from a damaged room's
    // copy: its corners are new, so none of the marks it inherited describe it.
    if (!old) { delete r.damage; continue; }
    const spans = r.damage?.wallSpans;
    if (!spans?.length) continue;
    const byWall = new Map<number, Iv[]>();
    const names = wallNames(old ?? r);
    for (const s of spans) {
      const now = resolveSpan(r, s);
      const was = old ? resolveSpan(old, s) : null;
      if (!now) {
        dropped.push({ rid: r.id, roomName: r.name, wall: names.get(was?.vid ?? -1) ?? 'wall', fromFt: s.fromFt, toFt: s.toFt });
        continue;
      }
      const wholeBefore = !!was && was.fromFt <= TOL() && was.toFt >= was.lengthFt - TOL();
      const from = wholeBefore ? 0 : Math.min(now.fromFt, now.lengthFt);
      const to = wholeBefore ? now.lengthFt : Math.min(now.toFt, now.lengthFt);
      if (to - from < MIN()) {
        dropped.push({ rid: r.id, roomName: r.name, wall: wallNames(r).get(now.vid) ?? 'wall', fromFt: s.fromFt, toFt: s.toFt });
        continue;
      }
      byWall.set(now.vid, [...(byWall.get(now.vid) ?? []), [Math.max(0, from), to]]);
    }
    const next: WallSpan[] = [];
    for (const [vid, ivs] of byWall) union(ivs).forEach(([a, b]) => next.push(spanOf(r, vid, a, b)));
    // Stay in the room's corner order, so equal marks compare equal.
    next.sort((x, y) => r.corners.findIndex((c) => idKey(c.id) === x.a) - r.corners.findIndex((c) => idKey(c.id) === y.a) || x.fromFt - y.fromFt);
    if (JSON.stringify(next) === JSON.stringify(spans)) continue;
    const d = ensure(r);
    d.wallSpans = next;
    syncWalls(d);
  }
  return dropped;
}

/** "Bedroom: N wall, E wall dropped: the wall is gone." */
export function droppedText(dropped: DroppedSpan[]): string {
  const rooms = [...new Set(dropped.map((x) => x.roomName))];
  const walls = dropped.map((x) => `${x.wall} of ${x.roomName}`);
  return `Damage on ${walls.join(', ')} dropped: ${dropped.length > 1 || rooms.length > 1 ? 'those walls changed' : 'the wall changed'}.`;
}

// ---------------------------------------------------------------- naming walls

/**
 * Compass name of each wall by its outward direction in the plan frame (north is up in the
 * stored plan). Two walls facing the same way are numbered left to right, top to bottom.
 */
export function wallNames(r: Room): Map<number, string> {
  const out = new Map<number, string>();
  const groups = new Map<string, WallExtent[]>();
  for (const w of roomWalls(r)) {
    const n = outN(r, w.a, w.b);
    const dir = Math.abs(n.x) >= Math.abs(n.y) ? (n.x > 0 ? 'E' : 'W') : (n.y < 0 ? 'N' : 'S');
    groups.set(dir, [...(groups.get(dir) ?? []), w]);
  }
  for (const [dir, ws] of groups) {
    const horizontal = dir === 'N' || dir === 'S';
    ws.sort((p, q) => (horizontal ? (p.a.x + p.b.x) - (q.a.x + q.b.x) : (p.a.y + p.b.y) - (q.a.y + q.b.y)));
    ws.forEach((w, i) => out.set(w.vid, ws.length > 1 ? `${dir} wall ${i + 1}` : `${dir} wall`));
  }
  return out;
}

const ft1 = (v: number): string => v.toFixed(1);

export interface WallDamageSummary { text: string; names: string[]; damagedFt: number; ofFt: number; mode: WallMode }

/** "Walls: N wall, E wall (5.8 of 12.0 ft)" for the room editor; null when no wall is marked. */
export function wallDamageSummary(r: Room): WallDamageSummary | null {
  const mode = wallMode(r);
  if (mode === 'none') return null;
  const names = wallNames(r);
  if (mode === 'all') {
    const total = roomWalls(r).reduce((s, w) => s + w.lengthFt, 0);
    return { text: `Walls: all walls (${ft1(total)} ft)`, names: [...names.values()], damagedFt: total, ofFt: total, mode };
  }
  const per = new Map<number, number>();
  for (const x of damagedStretches(r)) per.set(x.vid, (per.get(x.vid) ?? 0) + (x.toFt - x.fromFt));
  const walls = roomWalls(r).filter((w) => per.has(w.vid));
  const damagedFt = walls.reduce((s, w) => s + (per.get(w.vid) ?? 0), 0);
  const ofFt = walls.reduce((s, w) => s + w.lengthFt, 0);
  const list = walls.map((w) => names.get(w.vid) ?? 'wall');
  return { text: `Walls: ${list.join(', ')} (${ft1(damagedFt)} of ${ft1(ofFt)} ft)`, names: list, damagedFt, ofFt, mode };
}

// ---------------------------------------------------------------- drawing

/** One damaged stretch as the screen draws it: its two ends on the wall's centerline, and the wall's outward unit normal `n`. */
export interface DamageStroke { rid: string; vid: number; fromFt: number; toFt: number; p0: Pt; p1: Pt; thickFt: number; implicit: boolean; n: Pt }

/** The damaged stretches of a room, as world points on the wall band's centerline. */
export function damageStrokes(r: Room, thickFt: (vid: number) => number): DamageStroke[] {
  const implicit = wallMode(r) === 'all';
  const A = area(r);
  return damagedStretches(r).map((x) => {
    const w = roomWalls(r).find((q) => q.vid === x.vid)!;
    const u = unit(w.a, w.b), n = outN(r, w.a, w.b, A);
    const t = thickFt(x.vid);
    const at = (s: number): Pt => ({ x: w.a.x + u.x * s + n.x * (t / 2), y: w.a.y + u.y * s + n.y * (t / 2) });
    return { rid: r.id, vid: x.vid, fromFt: x.fromFt, toFt: x.toFt, p0: at(x.fromFt), p1: at(x.toFt), thickFt: t, implicit, n };
  });
}

// ---------------------------------------------------------------- copy damage

export interface CopyDamageResult {
  marks: DamageMarks;
  /** Spans carried to the target / spans the source had. */
  carried: number;
  of: number;
  /** The line to show: what was copied and what was not. */
  message: string;
}

/**
 * "Copy from another room" for damage: the areas always; the wall spans only for walls the target
 * has with the same corner pair (clipped to the target's wall). When the source marked walls but
 * none of its spans fit, the target gets walls as every wall and the message says so.
 */
export function copyDamage(source: DamageMarks, target: Room): CopyDamageResult {
  const A = AREAS();
  const areas = [...(source.damagedAreas ?? [])];
  const marks: DamageMarks = { damagedAreas: areas };
  const spans = source.wallSpans ?? [];
  if (!spans.length) {
    if (areas.includes(A.walls)) marks.markedOn = 'checklist';
    return { marks, carried: 0, of: 0, message: 'Damage copied.' };
  }
  const carried: WallSpan[] = [];
  for (const s of spans) {
    const res = resolveSpan(target, s);
    if (!res) continue;
    const to = Math.min(res.toFt, res.lengthFt), from = Math.max(0, res.fromFt);
    if (to - from >= MIN()) carried.push(spanOf(target, res.vid, from, to));
  }
  if (!carried.length) {
    marks.markedOn = 'checklist';
    marks.wallSpans = [];
    return {
      marks, carried: 0, of: spans.length,
      message: 'Areas copied. This room has no wall with the same corners, so walls are marked all round: mark the damaged walls on the plan.',
    };
  }
  marks.wallSpans = carried;
  marks.markedOn = 'sketch';
  return {
    marks, carried: carried.length, of: spans.length,
    message: carried.length === spans.length
      ? 'Damage copied, including the wall marks.'
      : `Damage copied. ${carried.length} of ${spans.length} wall marks fit this room; the rest were left out.`,
  };
}
